import {
  HHDivider,
  HHInfoGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import dayjs from 'dayjs';
import { SubmissionRemitsType } from '../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';
import { formatCurrency } from '../../../utils/currency-helpers';
import { displayDateTime } from '../../../utils/date-helper';

interface SubmissionDetailsProps {
  submissionsDetails: SubmissionRemitsType[];
  displayLocal: boolean;
  currency: string | null;
}

const SubmissionDetails = ({
  submissionsDetails,
  displayLocal,
  currency,
}: SubmissionDetailsProps): JSX.Element => (
  <HHStack spacing={2}>
    {submissionsDetails.length > 0 ? (
      submissionsDetails.map(sub => (
        <HHStack
          spacing={2}
          key={sub.id}
          bgcolor={(theme): string => theme.palette.background.default}
          padding={2}
          borderRadius={2}
        >
          <HHTypography
            hhVariant="variant-bypass"
            variant="h5"
            textTransform="capitalize"
          >
            Submission Id - {sub.id}
          </HHTypography>
          <HHInfoGrid
            data={[
              {
                content: String(sub.transactionId),
                heading: 'Transaction ID',
                copyable: true,
              },
              {
                content: sub.status,
                heading: 'Status',
                copyable: false,
              },
              {
                content: formatCurrency(sub.submissionAmount, currency),
                heading: 'Amount',
                copyable: false,
              },
              {
                content: dayjs(sub.submissionDate).format('L'),
                heading: 'Date',
                copyable: false,
              },
              {
                content: displayDateTime(sub.createdAt, {
                  displayLocal,
                }),
                heading: 'Created At',
                copyable: false,
              },
            ]}
            divided={false}
            justifyBetween={true}
          />
          <HHTypography hhVariant="subtitle1">Remits</HHTypography>
          {sub.remits.length > 0 ? (
            sub.remits.map(remit => (
              <HHStack spacing={1} key={remit.id}>
                <HHInfoGrid
                  data={[
                    {
                      content: formatCurrency(
                        remit.patientPaymentAmount,
                        currency,
                      ),
                      heading: 'Patient Payment',
                      copyable: false,
                    },
                    {
                      content: formatCurrency(remit.paymentAmount, currency),
                      heading: 'Payment Amount',
                      copyable: false,
                    },
                    {
                      content: remit.paymentDate
                        ? dayjs(remit.paymentDate).format('L')
                        : '--',
                      heading: 'Payment Date',
                      copyable: false,
                    },
                    {
                      content: remit.icn || '--',
                      heading: 'ICN',
                      copyable: true,
                    },
                    {
                      content: dayjs(remit.responseDate).format('L'),
                      heading: 'Response Date',
                      copyable: false,
                    },
                    {
                      content: displayDateTime(remit.createdAt, {
                        displayLocal,
                      }),
                      heading: 'Created At',
                      copyable: false,
                    },
                  ]}
                  divided={false}
                  justifyBetween={true}
                />
                <HHDivider />
              </HHStack>
            ))
          ) : (
            <HHTypography hhVariant="muted">No remits</HHTypography>
          )}
          <HHTypography hhVariant="subtitle1">Corrections</HHTypography>
          {sub.corrections && sub.corrections.length > 0 ? (
            sub.corrections.map(correction => (
              <HHStack spacing={1} key={correction.netsuiteId}>
                <HHInfoGrid
                  data={[
                    {
                      content: correction.netsuiteId,
                      heading: 'Netsuite Id',
                      copyable: true,
                    },
                    {
                      content: capitalizeFirstLetterAndSpaceSnakeString(
                        correction.correctionsType,
                      ),
                      heading: 'Type',
                      copyable: false,
                    },
                    {
                      content: formatCurrency(
                        correction.correctionAmount,
                        currency,
                      ),
                      heading: 'Amount',
                      copyable: false,
                    },
                    {
                      content: correction.lastModifiedBy,
                      heading: 'Last Modified By',
                      copyable: false,
                    },
                    {
                      content: displayDateTime(correction.createdAt, {
                        displayLocal,
                      }),
                      heading: 'Created At',
                      copyable: false,
                    },
                  ]}
                  divided={false}
                  justifyBetween={true}
                />
                <HHDivider />
              </HHStack>
            ))
          ) : (
            <HHTypography hhVariant="muted">No corrections</HHTypography>
          )}
        </HHStack>
      ))
    ) : (
      <HHTypography hhVariant="muted">No submissions</HHTypography>
    )}
  </HHStack>
);

export default SubmissionDetails;
